import { toast } from 'sonner';
import { TrackedDropdownMenuItem } from 'src/components/tracked';
import { graphql } from 'src/gql';
import { useConfirmationDialog } from 'src/hooks/use-confirmation-dialog';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

const ResetDemoHouseholdMutation = graphql(/* GraphQL */ `
  mutation ResetDemoHousehold($input: ResetDemoHouseholdInput!) {
    resetDemoHousehold(input: $input) {
      household {
        id
      }
      softError
    }
  }
`);

/**
 * Dropdown item that resets a demo household after confirmation. The confirmation is opened via
 * `useConfirmationDialog`, which renders at the app level — so the dropdown closing on select doesn't
 * unmount it (the reason the dialog used to live outside this component).
 */
export function ResetDemoHouseholdMenuItem({ householdId }: { readonly householdId: string }) {
  const showConfirmation = useConfirmationDialog();

  const [resetDemoHousehold] = useMutationWithErrorLogging(ResetDemoHouseholdMutation);

  const handleReset = async () => {
    try {
      const res = await resetDemoHousehold({ variables: { input: { householdId } } });

      if (res.data?.resetDemoHousehold?.softError) {
        toast.error('Failed to reset demo household');
      }

      window.location.reload();
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleSelect = () => {
    showConfirmation({
      title: 'Reset household',
      description: (
        <>
          This will reset the household information back to its defaults. All tasks, documents and signatures will be
          deleted.
          <br />
          <br />
          Are you sure you want to reset the household?
        </>
      ),
      confirmationLabel: 'Reset',
      confirmationVariant: 'destructive',
      onConfirmation: handleReset,
    });
  };

  return (
    <TrackedDropdownMenuItem elementName="advisor-dash-sidebar-reset-demo-household-button" onClick={handleSelect}>
      <span className="text-destructive">Reset household</span>
    </TrackedDropdownMenuItem>
  );
}
